// src/components/admin/InventoryDialog.jsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { inventoryApi, skuApi, productApi } from '../../api';

// 五种库存调整类型，各自要求的字段不一样：
// restock（进货）——必填成本价，可选生产/到期日期
// loss（损耗/盘点减少）——必须从现有批次里选一个来扣
// found（盘点发现多出来）——关联现有批次，或者新建一条（填估算成本价）
// return_restock（可上架退货）——加回到某个现有批次（比如客户退回的这批，选中该商品的对应批次）
// return_writeoff（报废退货）——不涉及批次，只需要填写原因备注
const TYPE_OPTIONS = [
  { value: 'restock',        label: '进货',       color: 'green'  },
  { value: 'loss',           label: '损耗/盘亏',  color: 'red'    },
  { value: 'found',          label: '盘盈',       color: 'blue'   },
  { value: 'return_restock', label: '可上架退货', color: 'teal'   },
  { value: 'return_writeoff',label: '报废退货',   color: 'gray'   },
];

const colorClasses = {
  green: 'border-green-500 bg-green-50 text-green-700',
  red:   'border-red-500 bg-red-50 text-red-700',
  blue:  'border-blue-500 bg-blue-50 text-blue-700',
  teal:  'border-teal-500 bg-teal-50 text-teal-700',
  gray:  'border-gray-500 bg-gray-100 text-gray-700',
};

const InventoryDialog = ({ product, onClose, onSuccess }) => {
  const { language } = useApp();
  const t = translations[language];

  const [changeType, setChangeType] = useState('restock');
  const [quantity, setQuantity]     = useState('');
  const [notes, setNotes]           = useState('');
  const [loading, setLoading]       = useState(false);

  // restock / found（新建批次）专用
  const [costPrice, setCostPrice]         = useState('');
  const [productionDate, setProductionDate] = useState('');
  const [expiryDate, setExpiryDate]         = useState('');

  // loss / found（关联现有） / return_restock 专用：需要先选一个具体批次
  const [batches, setBatches]         = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  // found 类型下，"关联现有批次" 和 "新建批次" 二选一
  const [foundMode, setFoundMode] = useState('existing'); // 'existing' | 'new'

  // 多规格支持：打开弹窗时先查一遍这个商品有没有规格
  const [specs, setSpecs] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(true);
  const [selectedSkuId, setSelectedSkuId] = useState('');

  const isMultiSpec = skus.length > 0;
  const selectedSku = skus.find(s => String(s.id) === String(selectedSkuId));

  // 这几种类型需要先选批次，需要拉批次列表
  const needsBatchList = ['loss', 'return_restock'].includes(changeType)
    || (changeType === 'found' && foundMode === 'existing');
  // 这几种类型需要填成本价
  const needsCostPrice = changeType === 'restock'
    || (changeType === 'found' && foundMode === 'new');

  useEffect(() => {
    let cancelled = false;
    const loadSpecs = async () => {
      setLoadingSkus(true);
      try {
        const res = await skuApi.getProductSpecs(product.id);
        if (!cancelled && res.success && res.data.has_specs) {
          setSpecs(res.data.specs || []);
          setSkus(res.data.skus || []);
        }
      } catch (err) {
        console.error('加载规格失败:', err);
        // 查询失败就当普通单规格商品处理，不阻塞库存调整这个更基础的功能
      } finally {
        if (!cancelled) setLoadingSkus(false);
      }
    };
    loadSpecs();
    return () => { cancelled = true; };
  }, [product.id]);

  // 需要批次列表的类型 / 多规格选定了具体SKU之后，去查这个商品（或SKU）当前有哪些可用批次
  useEffect(() => {
    if (!needsBatchList) { setBatches([]); setSelectedBatchId(''); return; }
    if (isMultiSpec && !selectedSkuId) { setBatches([]); return; }

    let cancelled = false;
    const loadBatches = async () => {
      setLoadingBatches(true);
      try {
        const res = await productApi.getAvailableBatches(product.id, isMultiSpec ? selectedSkuId : undefined);
        if (!cancelled && res.success) setBatches(res.data || []);
      } catch (err) {
        console.error('加载批次失败:', err);
      } finally {
        if (!cancelled) setLoadingBatches(false);
      }
    };
    loadBatches();
    return () => { cancelled = true; };
  }, [needsBatchList, isMultiSpec, selectedSkuId, product.id]);

  // 拼出SKU的规格标签，比如"红色 / S"
  const getSkuLabel = (sku) => {
    if (!sku.spec_values || sku.spec_values.length === 0) return '-';
    return sku.spec_values.map(sv => {
      const spec = specs.find(s => s.id === sv.spec_id);
      const value = spec?.values?.find(v => v.id === sv.spec_value_id);
      return value?.value || '-';
    }).join(' / ');
  };

  const currentStock = isMultiSpec
    ? (selectedSku ? selectedSku.stock : null)
    : product.stock;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quantity || parseInt(quantity) <= 0) {
      alert('请输入有效的数量');
      return;
    }
    if (isMultiSpec && !selectedSkuId) {
      alert('请先选择要调整的具体规格');
      return;
    }
    if (needsCostPrice && (!costPrice || parseFloat(costPrice) < 0)) {
      alert('请填写成本价');
      return;
    }
    if (needsBatchList && !selectedBatchId) {
      alert('请选择批次');
      return;
    }
    if (changeType === 'return_writeoff' && !notes.trim()) {
      alert('报废退货请填写原因备注');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        product_id: product.id,
        sku_id: isMultiSpec ? parseInt(selectedSkuId) : undefined,
        quantity: parseInt(quantity),
        change_type: changeType,
        notes: notes || undefined,
      };
      if (needsCostPrice) payload.cost_price = parseFloat(costPrice);
      if (changeType === 'restock') {
        if (productionDate) payload.production_date = productionDate;
        if (expiryDate) payload.expiry_date = expiryDate;
      }
      if (needsBatchList) payload.batch_id = parseInt(selectedBatchId);

      // 现在统一走 inventoryApi.adjust（支持可选的sku_id），
      // 不再区分"普通商品用inventoryApi、SKU商品用skuApi.adjustSkuStock"——
      // 后端 adjustInventory 已经统一处理这两种情况，skuApi.adjustSkuStock 这个老接口
      // 还是旧的、不接入批次系统的简单版本，这里不应该再调用它
      console.log('📦 提交库存调整:', payload);
      const res = await inventoryApi.adjust(payload);

      if (res.success) {
        alert('库存调整成功！');
        onSuccess();
        onClose();
      } else {
        alert(res.message || t.operationFailed || '操作失败');
      }
    } catch (err) {
      console.error('❌ 库存调整失败:', err);
      alert(err.message || t.operationFailed || '操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const getProductName = () => {
    switch (language) {
      case 'en': return product.name_en || product.name;
      case 'km': return product.name_km || product.name;
      default:   return product.name;
    }
  };

  const inputClass = "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500";
  const labelClass = "block text-sm font-medium mb-1";

  const outOfRange = (changeType === 'loss')
    && quantity && currentStock != null && parseInt(quantity) > currentStock;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b p-6 flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-xl font-bold">库存调整</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 商品信息 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-medium text-gray-800">{getProductName()}</p>
                <p className="text-sm text-gray-500">{t.barcode}: {product.barcode}</p>
              </div>
              {!isMultiSpec && (
                <div className="text-right">
                  <p className="text-sm text-gray-600">当前库存</p>
                  <p className="text-2xl font-bold text-blue-600">{product.stock}</p>
                </div>
              )}
            </div>
          </div>

          {/* 多规格商品：先选具体规格 */}
          {loadingSkus ? (
            <div className="text-center py-4 text-sm text-gray-400">加载规格中...</div>
          ) : isMultiSpec && (
            <div>
              <label className={labelClass}>
                选择规格 <span className="text-red-500">*</span>
                <span className="text-xs text-gray-400 ml-1 font-normal">（该商品有多个规格，库存/批次按规格分别管理）</span>
              </label>
              <select
                value={selectedSkuId}
                onChange={e => { setSelectedSkuId(e.target.value); setSelectedBatchId(''); }}
                className={inputClass}
                required
              >
                <option value="">请选择规格</option>
                {skus.map(sku => (
                  <option key={sku.id} value={sku.id}>
                    {getSkuLabel(sku)}（当前库存：{sku.stock}）
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 操作类型：五选一 */}
          <div>
            <label className={labelClass}>操作类型</label>
            <div className="grid grid-cols-2 gap-2">
              {TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setChangeType(opt.value); setSelectedBatchId(''); }}
                  className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                    changeType === opt.value ? colorClasses[opt.color] : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 数量 */}
          <div>
            <label className={labelClass}>数量</label>
            <input
              type="number" value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className={inputClass}
              placeholder="10" min="1" required
              disabled={isMultiSpec && !selectedSkuId}
            />
          </div>

          {/* 成本价：进货 / 盘盈-新建批次 需要 */}
          {needsCostPrice && (
            <div>
              <label className={labelClass}>
                {changeType === 'restock' ? '成本价 (USD)' : '估算成本价 (USD)'} <span className="text-red-500">*</span>
              </label>
              <input
                type="number" step="0.01" min="0" value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
                className={inputClass} placeholder="0.00" required
              />
            </div>
          )}

          {/* 进货专用：生产/到期日期（可选，商品开了保质期管理才需要填） */}
          {changeType === 'restock' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>生产日期（可选）</label>
                <input type="date" value={productionDate}
                  onChange={e => setProductionDate(e.target.value)}
                  className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>到期日期（可选）</label>
                <input type="date" value={expiryDate}
                  onChange={e => setExpiryDate(e.target.value)}
                  className={inputClass} />
              </div>
            </div>
          )}

          {/* 盘盈：关联现有批次 or 新建批次 */}
          {changeType === 'found' && (
            <div>
              <label className={labelClass}>这批货是哪来的</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setFoundMode('existing')}
                  className={`px-3 py-2 rounded-lg border-2 text-sm transition-colors ${
                    foundMode === 'existing' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'
                  }`}>
                  确定是之前某批漏记的
                </button>
                <button type="button" onClick={() => setFoundMode('new')}
                  className={`px-3 py-2 rounded-lg border-2 text-sm transition-colors ${
                    foundMode === 'new' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'
                  }`}>
                  说不清是哪批，新建一条
                </button>
              </div>
            </div>
          )}

          {/* 批次选择：损耗/盘亏、盘盈-关联现有、可上架退货 都需要 */}
          {needsBatchList && (
            <div>
              <label className={labelClass}>
                选择批次 <span className="text-red-500">*</span>
                {changeType === 'loss' && <span className="text-xs text-gray-400 ml-1 font-normal">（从哪个批次扣）</span>}
                {changeType === 'return_restock' && <span className="text-xs text-gray-400 ml-1 font-normal">（加回到哪个批次）</span>}
              </label>
              {loadingBatches ? (
                <p className="text-sm text-gray-400 py-2">加载批次中...</p>
              ) : batches.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">该商品当前没有可用批次</p>
              ) : (
                <select value={selectedBatchId} onChange={e => setSelectedBatchId(e.target.value)}
                  className={inputClass} required>
                  <option value="">请选择批次</option>
                  {batches.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.batch_no}（剩余 {b.quantity} 个{b.expiry_date ? `，到期 ${b.expiry_date}` : ''}）
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* 备注：报废退货必填，其他可选 */}
          <div>
            <label className={labelClass}>
              备注{changeType === 'return_writeoff' ? <span className="text-red-500"> *（请说明报废原因）</span> : '（可选）'}
            </label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              className={inputClass} rows="3"
              placeholder="输入备注..."
              required={changeType === 'return_writeoff'}
            />
          </div>

          {outOfRange && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">⚠️ 警告: 库存不足！</p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}>
              {t.cancel}
            </button>
            <button type="submit"
              disabled={loading || outOfRange || (isMultiSpec && !selectedSkuId)}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? '处理中...' : t.confirm}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryDialog;